import { ButterflyAdminGate } from '../modules/butterfly-world/ButterflyAdminGate';
import { secureUuid } from '../shared/utils/secure-uuid';
import React, { FC, useContext, Suspense, lazy } from 'react';
import { Route, Routes, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import LoginPage from '@pages/Login';
const OpenByCode = lazy(() => import('../pages/OpenByCode'));

import { Layout } from '@shared/components';
import type { GameKey } from '@shared/types';
import { useYandexMetric } from '@shared/hooks/useYandexMetric';

import { authContext } from '@store/index';
import { AquariumLoadingScreen } from '../components/aquarium/AquariumLoadingScreen';

import ConditionalRoute from './ConditionalRoute';
import { ConstructorByTitle } from './ConstructorByTitle';
import { ConstructorRedirect } from './ConstructorRedirect';

// Master
const MasterPage = lazy(() => import('@pages/Master'));
// =========================================================================
// 2. Lazy Loading (Загружается только по требованию)
// =========================================================================

// Игровая страница (Player) - выносим в отдельный чанк, чтобы не грузить админам
const GamePage = lazy(() => import('@pages/Game'));
const PhotoPuzzlePreviewPage = lazy(() => import('../pages/PhotoPuzzlePreview'));

// Игры
const MinerGameWrapper = lazy(() => import('@pages/_Games/prod/Miner/MinerGameWrapper'));
const CapybaraGame = lazy(() => import('src/pages/_Games/in dev/Capybara'));
const SuccessPage = lazy(() => import('@pages/Success'));
const FlappyGame = lazy(() => import('src/pages/_Games/in dev/Flappy'));
const PhotoPuzzleGameWrapper = lazy(() => import('src/pages/_Games/prod/PhotoPuzzle/PhotoPuzzleGameWrapper'));
const ScratchCardGameWrapper = lazy(() => import('src/pages/_Games/in dev/ScratchCard/ScratchCardGameWrapper'));
const CodeLockGameWrapper = lazy(() => import('src/pages/_Games/in dev/CodeLock/CodeLockGameWrapper'));
const FrogZumaGameWrapper = lazy(() => import('src/pages/_Games/in dev/FrogZuma/FrogZumaGameWrapper'));
const MillionaireGameWrapper = lazy(() => import('src/pages/_Games/in dev/Millionaire/MillionaireGameWrapper'));

// Тестовые страницы
const ImageEditorTestPage = lazy(() => import('src/pages/_Games/in dev/ImageEditorTest'));
const PageLoadTest = lazy(() => import('@shared/components/PageLoadTest'));
const CatAnimationTestPage = lazy(() => import('src/pages/_Games/in dev/CatAnimationTest'));

// Конструкторы игр
const ClientPageMaze = lazy(() => import('src/pages/Constructors/prod/ClientMaze'));
const ClientPageMazeLite = lazy(() => import('src/pages/Constructors/prod/ClientMazeLite'));
const ClientPageMazeFree = lazy(() => import('src/pages/Constructors/prod/ClientMazeFree'));
const ClientPageMemory = lazy(() => import('src/pages/Constructors/prod/ClientMemory'));
const ClientPageMemoryLite = lazy(() => import('src/pages/Constructors/prod/ClientMemoryLite'));
const ClientPageMiner = lazy(() => import('src/pages/Constructors/prod/ClientMiner/index2'));
const ClientPageSlot = lazy(() => import('src/pages/Constructors/prod/ClientSlot'));
const ClientPageCapybara = lazy(() => import('src/pages/Constructors/in dev/ClientCapybara'));
const ClientPageFlappy = lazy(() => import('src/pages/Constructors/in dev/ClientFlappy'));
const ClientPagePhotoPuzzle = lazy(() => import('src/pages/Constructors/prod/ClientPhotoPuzzle'));
const ClientPageScratchCard = lazy(() => import('src/pages/Constructors/in dev/ClientScratchCard'));
const ClientPageSafeBreaker = lazy(() => import('src/pages/Constructors/in dev/ClientSafeBreaker'));
const ClientPageFrogZuma = lazy(() => import('src/pages/Constructors/in dev/ClientFrogZuma'));
const ClientPageCodeLock = lazy(() => import('src/pages/Constructors/in dev/ClientCodeLock'));

// Partner страницы
const PartnerDashboard = lazy(() => import('@pages/Partner/Dashboard'));
const PartnerLoginForm = lazy(() => import('@pages/Partner/LoginForm'));

// Admin страницы
const WorldsAdmin = lazy(() => import('@pages/Admin/Worlds'));
const AdminLoginPage = lazy(() => import('@pages/Admin/Login'));
const AdminDashboard = lazy(() => import('@pages/Admin/Dashboard'));
const PartnersManagement = lazy(() => import('@pages/Admin/Partners'));
const GameTemplateConstructor = lazy(() => import('@pages/Admin/GameTemplateConstructor'));
const SongServiceAdmin = lazy(() => import('@pages/Admin/SongService'));
const HalloweenRoute = lazy(() => import('../modules/halloween-world/HalloweenRoute'));
const ButterflyRecognitionLabPage = lazy(() => import('../modules/butterfly-world/ButterflyRecognitionLabPage'));
const ButterflyMeadowDemoPage = lazy(() => import('../pages/ButterflyMeadow/ButterflyMeadowDemoPage'));
const ButterfliesRoute = lazy(() => import('../modules/butterfly-world/ButterfliesRoute'));
const NewYearTreeRoute = lazy(() => import('../modules/new-year-tree/NewYearTreeRoute'));
const PetalLabPage = lazy(() => import('../modules/butterfly-meadow/PetalLabPage'));
import { ButterflyBootScreen } from '../modules/butterfly-world/ButterflyBootScreen';
import { TreeBootScreen } from '../modules/new-year-tree/TreeBootScreen';
import { newYearTree3dRoutes } from '../modules/new-year-tree-3d/routes';
const BotDashboard = lazy(() => import('@pages/Admin/BotDashboard'));
const BotUsers = lazy(() => import('@pages/Admin/BotUsers'));
const BotBroadcast = lazy(() => import('@pages/Admin/BotBroadcast'));
const BotSettings = lazy(() => import('@pages/Admin/BotSettings'));

// Аквариум живёт в приложении сервера, а не в storefront.
const AquariumExperience = lazy(() => import('@pages/Aquarium/AquariumExperience').then((module) => ({ default: module.AquariumExperience })));

// Выбор дизайна
const GameDesignsPage = lazy(() => import('@pages/GameDesigns'));

const SongGeneratorPage = lazy(() => import('src/components/SongGenerator/index').then(module => ({ default: module.SongGenerator })));

// =========================================================================
// 3. Вспомогательные компоненты
// =========================================================================

const LoadingFallback = () => (
  <>
    <style>{`
      @keyframes router-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .router-loading-spinner {
        width: 50px;
        height: 50px;
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-top: 4px solid #888;
        border-radius: 50%;
        animation: router-spin 1s linear infinite;
        margin-bottom: 20px;
      }
    `}</style>
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontSize: '18px',
      color: '#888'
    }}>
      {/* <div className="router-loading-spinner" />
      <div>Loading...</div> */}
    </div>
  </>
);

// Маппинг типов игр на компоненты
const GAME_TYPE_COMPONENTS = {
  memory: ClientPageMemory,
  memorylite: ClientPageMemoryLite,
  'memory-lite': ClientPageMemoryLite,
  slot: ClientPageSlot,
  maze: ClientPageMaze,
  mazelite: ClientPageMazeLite,
  'maze-lite': ClientPageMazeLite,
  mazefree: ClientPageMazeFree,
  miner: ClientPageMiner,
  capybara: ClientPageCapybara,
  flappy: ClientPageFlappy,
  photopuzzle: ClientPagePhotoPuzzle,
  'photo-puzzle': ClientPagePhotoPuzzle,
  scratchcard: ClientPageScratchCard,
  'scratch-card': ClientPageScratchCard,
  codelock: CodeLockGameWrapper,
  'code-lock': CodeLockGameWrapper,
  safebreaker: ClientPageSafeBreaker,
  'safe-breaker': ClientPageSafeBreaker,
  frogzuma: ClientPageFrogZuma,
  'frog-zuma': ClientPageFrogZuma,
} as const;

type GameType = keyof typeof GAME_TYPE_COMPONENTS;

// Маппинг конструкторов для маршрута /constructor/:type
const CONSTRUCTOR_COMPONENTS: Record<string, React.ComponentType<any>> = {
  memory: ClientPageMemory,
  memorylite: ClientPageMemoryLite,
  'memory-lite': ClientPageMemoryLite,
  maze: ClientPageMaze,
  mazelite: ClientPageMazeLite,
  'maze-lite': ClientPageMazeLite,
  mazefree: ClientPageMazeFree,
  miner: ClientPageMiner,
  capybara: ClientPageCapybara,
  flappy: ClientPageFlappy,
  'photo-puzzle': ClientPagePhotoPuzzle,
  photopuzzle: ClientPagePhotoPuzzle,
  scratchcard: ClientPageScratchCard,
  'scratch-card': ClientPageScratchCard,
  'code-lock': ClientPageCodeLock,
  codelock: ClientPageCodeLock,
  'safe-breaker': ClientPageSafeBreaker,
  safebreaker: ClientPageSafeBreaker,
  'frog-zuma': ClientPageFrogZuma,
  frogzuma: ClientPageFrogZuma,
  slot: ClientPageSlot,
};

// Функция нормализации типа игры для конструктора и определения имени игры
const normalizeTypeForConstructor = (type: string): string => {
  const normalized = (type || '').toLowerCase().trim();
  switch (normalized) {
    case 'memory':
      return 'Memory';
    case 'photopuzzle':
    case 'photo-puzzle':
      return 'photoPuzzle';
    case 'scratchcard':
    case 'scratch-card':
      return 'scratchCard';
    case 'codelock':
    case 'code-lock':
      return 'codeLock';
    case 'safebreaker':
    case 'safe-breaker':
      return 'safeBreaker';
    case 'frogzuma':
    case 'frog-zuma':
      return 'frogZuma';
    case 'mazefree':
      return 'mazeFree';
    case 'mazelite':
    case 'maze-lite':
      return 'MazeLite';
    case 'maze':
      return 'maze';
    case 'miner':
      // Возможно, стоит проверить имя в БД для майнера. Обычно Miner?
      // Но возвращаем пока lowercase если так было, или Capitalized.
      // В router'е было return 'miner'.
      return 'miner';
    case 'capybara':
      return 'capybara';
    case 'flappy':
      return 'flappy';
    case 'slot':
      return 'slot';
    default:
      // Пытаемся сделать Capitalized как fallback
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

const DynamicConstructor: React.FC = () => {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const [authData] = useContext(authContext);
  const key = type || '';
  const Comp = CONSTRUCTOR_COMPONENTS[key];

  if (!Comp) {
    return <Navigate to="/login" replace />;
  }

  // Обработка freemium параметров
  const art = searchParams.get('art');
  const price = searchParams.get('price');

  if (art && !authData?.gameKey) {
    // Если есть артикул, но нет авторизации, создаем временный объект игры
    const tempGameKey: GameKey = {
      _id: 'temp',
      gameBuildId: secureUuid(), // Генерируем временный ID билда
      gameName: art || normalizeTypeForConstructor(key), // Пытаемся использовать артикул как имя для поиска настроек дизайна
      gameType: key,
      isPaid: false,
      articleId: art,
      price: price ? parseFloat(price) : 0,
      gameDayCount: 30, // Дефолтные значения
      editDayCount: 7,
      isCreated: false,
    } as any;

    return <Comp gameKey={tempGameKey} />;
  }

  // Передаем gameKey из authData, если он есть
  return <Comp gameKey={authData?.gameKey || null} />;
};

const AquariumRoute: React.FC<{ mode: 'display' | 'add' | 'draw' | 'capture' | 'print' | 'manage' | 'moderator' }> = ({ mode }) => {
  const params = useParams<{ code?: string; secret?: string }>();
  const navigate = useNavigate();
  return <Suspense fallback={<AquariumLoadingScreen />}><AquariumExperience mode={mode} code={params.code} secret={params.secret} onNavigate={(path) => navigate(path)} /></Suspense>;
};

// Keep existing game links with ?key= working; manual entry accepts all projects.
const ProjectLogin: FC = () => {
  const [params] = useSearchParams();
  return params.get('key') ? <LoginPage /> : <OpenByCode />;
};

const Router: FC = () => {
  // Отслеживание навигации в Яндекс Метрике
  useYandexMetric();

  const [authData] = useContext(authContext);
  const queryParams = new URLSearchParams(window.location.search);
  const hasKeyInUrl = typeof window !== 'undefined' && Boolean(queryParams.get('key'));
  const hasArtInUrl = typeof window !== 'undefined' && Boolean(queryParams.get('art'));


  // Функция выбора компонента дашборда
  const getDashboardComponent = () => {
    if (!authData) return null;

    // Проверяем, есть ли мастер-код
    const gk = authData.gameKey as any;
    const isMasterCode = authData.masterKey || gk?.gameName === 'MasterCode';
    
    if (isMasterCode) {
      const gameType = gk?.gameType || 'memory';
      const hasGameBuild = gk?.gameBuild && gk?.gameBuildId;
      
      // Если игра уже создана (есть gameBuild), перенаправляем в конструктор
      if (hasGameBuild) {
        const normalizedType = normalizeTypeForConstructor(gameType);
        const key = gk?.key || (authData.masterKey as any)?.key;
        const keyParam = key ? `?key=${encodeURIComponent(key)}` : '';
        return <Navigate to={`/constructor/${encodeURIComponent(normalizedType)}${keyParam}`} replace />;
      }
      
      // Если игра еще не создана, перенаправляем на выбор дизайнов
      return <Navigate to={`/games/${encodeURIComponent(gameType)}?from=mastercode`} replace />;
    }

    if (authData.gameKey) {
      const gameType = (authData.gameKey.gameType || '').toLowerCase();
      const GameComponent = (GAME_TYPE_COMPONENTS as any)[gameType];

      if (GameComponent) {
        return <GameComponent gameKey={authData.gameKey} />;
      }
      return <ClientPageMemory gameKey={authData.gameKey} />;
    }

    return null;
  };

  return (
    // Оборачиваем ВСЕ роуты в один Suspense. 
    // Это позволяет React ждать загрузки любого ленивого компонента внутри Routes.
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* === Публичные роуты === */}
        <Route path="/open" element={<Suspense fallback={<div role="status">Открываем проект…</div>}><OpenByCode /></Suspense>} />
        <Route path="/login" element={<ProjectLogin />} />

        {/* === Защищенные роуты (Конструкторы) === */}
        <Route element={<Layout />}>
          <Route
            path="/"
            element={
              <ConditionalRoute condition={!!authData} redirectTo="/login">
                {getDashboardComponent()}
              </ConditionalRoute>
            }
          />
        </Route>
        
        {/* === Admin Роуты === */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/partners" element={<PartnersManagement />} />
        <Route path="/admin/game-templates" element={<GameTemplateConstructor />} />
        <Route path="/admin/music" element={<SongServiceAdmin />} />
        <Route path="/admin/worlds" element={<WorldsAdmin />} />
        <Route path="/admin/aquariums" element={<Navigate to="/admin/worlds" replace />} />
        <Route path="/admin/halloweens" element={<Navigate to="/admin/worlds" replace />} />
        <Route path="/admin/butterflies" element={<Navigate to="/admin/worlds" replace />} />
        <Route path="/admin/bot" element={<BotDashboard />} />
        <Route path="/admin/bot/settings" element={<BotSettings />} />
        <Route path="/admin/bot/users" element={<BotUsers />} />
        <Route path="/admin/bot/broadcast" element={<BotBroadcast />} />

        <Route path="/butterflies" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="manage" /></Suspense>} />
        <Route path="/butterflies/manage" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="manage" /></Suspense>} />
        <Route path="/butterflies/demo" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="demo" /></Suspense>} />
        <Route path="/butterflies/display/:code" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="display" /></Suspense>} />
        <Route path="/butterflies/add/:code" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="add" /></Suspense>} />
        <Route path="/butterflies/draw/:code" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="draw" /></Suspense>} />
        <Route path="/butterflies/capture/:code" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="capture" /></Suspense>} />
        <Route path="/butterflies/print/:code" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="print" /></Suspense>} />
        <Route path="/butterflies/moderator/:secret" element={<Suspense fallback={<ButterflyBootScreen />}><ButterfliesRoute mode="moderator" /></Suspense>} />
        <Route path="/new-year-tree" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="manage" /></Suspense>} />
        <Route path="/new-year-tree/manage" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="manage" /></Suspense>} />
        <Route path="/new-year-tree/demo" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="demo" /></Suspense>} />
        <Route path="/new-year-tree/display/:code" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="display" /></Suspense>} />
        <Route path="/new-year-tree/add/:code" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="add" /></Suspense>} />
        <Route path="/new-year-tree/draw/:code" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="draw" /></Suspense>} />
        <Route path="/new-year-tree/capture/:code" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="capture" /></Suspense>} />
        <Route path="/new-year-tree/print/:code" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="print" /></Suspense>} />
        <Route path="/new-year-tree/moderate/:code" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="moderate" /></Suspense>} />
        <Route path="/new-year-tree/moderator/:secret" element={<Suspense fallback={<TreeBootScreen />}><NewYearTreeRoute mode="moderator" /></Suspense>} />
        {newYearTree3dRoutes()}

        {import.meta.env.DEV ? <Route path="/butterflies/petal-lab" element={<Suspense fallback={null}><PetalLabPage /></Suspense>} /> : null}
        <Route path="/butterflies/recognition-lab" element={<Suspense fallback={<ButterflyBootScreen />}>
          {import.meta.env.DEV ? <ButterflyRecognitionLabPage /> : <ButterflyAdminGate><ButterflyRecognitionLabPage /></ButterflyAdminGate>}
        </Suspense>} />
        <Route path="/butterflies/demo" element={<Suspense fallback={<ButterflyBootScreen />}>
          {import.meta.env.DEV ? <ButterflyMeadowDemoPage /> : <ButterflyAdminGate><ButterflyMeadowDemoPage /></ButterflyAdminGate>}
        </Suspense>} />

        {/* Купленный аквариум и все рабочие ссылки обслуживаются сервером 3003. */}
        <Route path="/halloween" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="manage" /></Suspense>} />
        <Route path="/halloween/manage" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="manage" /></Suspense>} />
        <Route path="/halloween/display/:code" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="display" /></Suspense>} />
        <Route path="/halloween/print/:code" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="print" /></Suspense>} />
        <Route path="/halloween/add/:code" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="add" /></Suspense>} />
        <Route path="/halloween/draw/:code" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="draw" /></Suspense>} />
        <Route path="/halloween/capture/:code" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="capture" /></Suspense>} />
        <Route path="/halloween/moderator/:secret" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="moderator" /></Suspense>} />
        <Route path="/halloween/coloring-lab" element={<Suspense fallback={<div role="status">Загрузка Призрачного мира…</div>}><HalloweenRoute mode="lab" /></Suspense>} />
        <Route path="/aquarium" element={<AquariumRoute mode="manage" />} />
        <Route path="/aquarium/manage" element={<AquariumRoute mode="manage" />} />
        <Route path="/aquarium/display/:code" element={<AquariumRoute mode="display" />} />
        <Route path="/aquarium/print/:code" element={<AquariumRoute mode="print" />} />
        <Route path="/aquarium/add/:code" element={<AquariumRoute mode="add" />} />
        <Route path="/aquarium/capture/:code" element={<AquariumRoute mode="capture" />} />
        <Route path="/aquarium/draw/:code" element={<AquariumRoute mode="draw" />} />
        <Route path="/aquarium/moderator/:secret" element={<AquariumRoute mode="moderator" />} />
        
        {/* === Master Роут (не используем, редиректим на дизайны) === */}
        <Route
          path="/master"
          element={
            <Navigate to="/games/memory?from=mastercode" replace />
          }
        />

        {/* === Partner Роуты === */}
        <Route path="/partners" element={<PartnerLoginForm />} />
        <Route path="/partners/login" element={<PartnerLoginForm />} />
        <Route path="/partners/dashboard" element={<PartnerDashboard />} />
        
        {/* === Игровые роуты (Player) === */}
        <Route path="/game" element={<GamePage />} />
        <Route path="/preview/photo-puzzle" element={<PhotoPuzzlePreviewPage />} />
        <Route path="/success/:gameBuildId" element={<SuccessPage />} />
        <Route path="/games/:type" element={<GameDesignsPage />} />
        <Route element={<Layout />}>
          {/* Редирект по design параметру */}
          <Route path="/constructor" element={<ConstructorRedirect />} />
          
          <Route
            path="/constructor/:titleOrType"
            element={
              <ConditionalRoute condition={!!authData || hasKeyInUrl || hasArtInUrl} redirectTo="/login">
                <ConstructorByTitle constructorComponents={CONSTRUCTOR_COMPONENTS} />
              </ConditionalRoute>
            }
          />
        </Route>
        
        {/* Специфичные игры (Запускаются напрямую) */}
        <Route 
          path="/miner" 
          element={<MinerGameWrapper gameKey={null} gameSettings={null} />} 
        />
        <Route 
          path="/game/miner" 
          element={<MinerGameWrapper gameKey={null} gameSettings={null} />} 
        />
        <Route 
          path="/game/capybara" 
          element={<CapybaraGame gameKey={null} gameSettings={null} />} 
        />
        <Route 
          path="/game/flappy" 
          element={<FlappyGame gameKey={null} gameSettings={null} />} 
        />
        <Route 
          path="/game/photo-puzzle" 
          element={<PhotoPuzzleGameWrapper gameKey={null} />} 
        />
        <Route 
          path="/photo-puzzle" 
          element={<PhotoPuzzleGameWrapper gameKey={null} />} 
        />
        <Route 
          path="/game/scratch-card" 
          element={<ScratchCardGameWrapper gameKey={null} />} 
        />
        <Route 
          path="/scratch-card" 
          element={<ScratchCardGameWrapper gameKey={null} />} 
        />
        <Route 
          path="/game/code-lock" 
          element={<CodeLockGameWrapper gameKey={null} />} 
        />
        <Route 
          path="/code-lock" 
          element={<CodeLockGameWrapper gameKey={null} />} 
        />
        <Route
          path="/game/frog-zuma"
          element={<FrogZumaGameWrapper gameKey={null} />}
        />
        <Route
          path="/frog-zuma"
          element={<FrogZumaGameWrapper gameKey={null} />}
        />
        <Route
          path="/game/millionaire"
          element={<MillionaireGameWrapper gameKey={null} />}
        />
        <Route
          path="/millionaire"
          element={<MillionaireGameWrapper gameKey={null} />}
        />

        {/* === Тестовые роуты === */}
        <Route path="/test/image-editor" element={<ImageEditorTestPage />} />
        <Route path="/test/page-load" element={<PageLoadTest />} />
        <Route path="/test/cat-animation" element={<CatAnimationTestPage />} />

        {/* === AI Music Generator === */}
        <Route path="/music" element={<SongGeneratorPage />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
